import Mask from '../../tools/mask';
import Format from '../../tools/format';
import { formatColor, parseColor } from '../../tools/color';
import setIcon from '../define/setIcon';
import { hasPresence, presenceHref } from './maskPresence';

function resolveDicRef(value) {
  if (typeof value !== 'string') return value;

  const raw = value.trim();
  const unquoted = raw.replace(/^['"`\s]+|['"`\s]+$/g, '');
  if (!/^dic\./i.test(unquoted)) return value;

  const parts = unquoted.replace(/^dic\./i, '').split('.').filter(Boolean);
  let cursor = window?.dic;

  for (const part of parts) {
    if (!cursor || typeof cursor !== 'object') return value;
    if (Object.prototype.hasOwnProperty.call(cursor, part)) {
      cursor = cursor[part];
      continue;
    }

    const fallbackKey = Object.keys(cursor).find(
      (key) => String(key).toLowerCase() === part.toLowerCase()
    );
    if (!fallbackKey) return value;
    cursor = cursor[fallbackKey];
  }

  return typeof cursor === 'string' || typeof cursor === 'number' ? String(cursor) : value;
}

export default async (elem, options) => {
  if (options.mask && (typeof options.mask === 'object' || typeof options.mask === 'string')) {
    const format = new Format();
    let mask;

    const templates = ['cep', 'color', 'cpf', 'cnpj', 'creditCard', 'currency', 'date', 'dateTime', 'document', 'geodoc', 'integer',
      'ipv4', 'float', 'number', 'percent', 'phone', 'rg', 'time', 'timeFull', 'timeTotal', 'timeTotalFull',
      'timeTotalFullWithDays', 'textOnly'];

    if (elem.tagName !== 'INPUT') {
      templates.push('checkbox', 'radio', 'progress', 'tag');
    }

    if (typeof options.mask === 'object') {
      // A mesma definição de coluna pode ser renderizada muitas vezes. Não
      // altere a máscara declarada na config ao preparar uma célula.
      mask = { ...options.mask };
    } else if (templates.includes(options.mask)) {
      mask = { template: options.mask };
    } else {
      mask = { format: options.mask };
    }
    // Células da DataGrid recebem timestamp ISO. Formate antes da máscara
    // numérica de inputs para não converter 2026-10-03 em 20/26/1003.
    if (elem.tagName !== 'INPUT' && mask.template === 'dateTime') {
      const raw = elem.textContent?.trim() ?? '';
      const formattedDateTime = format.dateTime(raw);
      if (formattedDateTime !== 'NULL') elem.textContent = formattedDateTime;
      const isGridCell = elem.closest?.('td') !== null;
      elem.classList.remove('left', 'right');
      elem.classList.add(isGridCell ? 'center' : 'right');
      return elem;
    }

    if (mask && mask.direction === undefined) {
      mask.direction = 'left';
    }

    if (mask && mask.decimalPlaces === undefined) {
      mask.decimalPlaces = 2;
    }

    if (mask) {
      const optionsSrc = Array.isArray(mask.options)
        ? mask.options
        : Array.isArray(options?.options)
          ? options.options
          : null;

      if (Array.isArray(optionsSrc) && optionsSrc.length > 0) {
        mask.options ??= optionsSrc;
        mask.values ??= optionsSrc.map((item) => item?.value ?? item);
        mask.labels ??= optionsSrc.map((item) => item?.label ?? item?.text ?? item?.value ?? item);
      }
    }

    if (mask.template !== undefined && typeof mask.template === 'string') {
      switch (mask.template) {
        case 'cep':
          mask.format = '00000-000';
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : mask.format;
          break;

        case 'checkbox':
          // Presença: check quando o dado existe (e ação opcional por `link`), vazio quando não existe. Disponibilidade do canal, não validação do vínculo.
          if (mask.presence === true) {
            const raw = String(elem.textContent ?? '').trim();
            elem.textContent = '';
            if (hasPresence(raw)) {
              elem.classList.add('right');
              const href = mask.link ? presenceHref(mask.link, raw) : null;
              const holder = href ? document.createElement('a') : elem;
              if (href) { holder.href = href; holder.rel = 'noopener noreferrer'; if (mask.link === 'url') holder.target = '_blank'; elem.append(holder); }
              setIcon(holder, { icon: { type: 'solid', name: 'check green' } });
            }
            mask = null;
            break;
          }
          const cvalue = String(elem.textContent ?? '').trim().toLowerCase();
          const hasCustomValues = Array.isArray(mask.values) && mask.values.length > 0;
          const hasCustomIcons = Array.isArray(mask.icons) && mask.icons.length > 0;

          if (!hasCustomValues && !hasCustomIcons) {
            const isTrueValue = cvalue === 'true' || cvalue === '1';
            elem.textContent = '';
            elem.classList.add('right');
            setIcon(elem, {
              icon: isTrueValue
                ? { type: 'solid', name: 'check green' }
                : { type: 'solid', name: 'times red' }
            });
            mask = null;
            break;
          }

          if (!mask.values) mask.values = ['false', 'true'];
          if (!mask.icons) mask.icons = [
            { type: 'solid', name: 'times red' },
            { type: 'solid', name: 'check green' }
          ];

          for (const i in mask.values) {
            if (cvalue === String(mask.values[i]).trim().toLowerCase()) {
              elem.textContent = '';
              if (mask.icons[i]) {
                elem.classList.add('right');
                setIcon(elem, { icon: mask.icons[i] });
              }
            }
          }
          mask = null;
          break;

        case 'tag': {
          const rawValue = mask.value !== undefined && mask.value !== null
            ? mask.value
            : elem.textContent;
          const rawKey = String(rawValue ?? '').trim();
          const colorField = String(mask.colorField || '').trim();
          const rowColor = colorField
            ? String(options?.data?.[colorField] ?? '').trim()
            : '';
          const validRowColor = rowColor && (
            /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(rowColor)
            || globalThis.CSS?.supports?.('color', rowColor) === true
          );

          const modeEntry = mask.mode && typeof mask.mode === 'object' && !Array.isArray(mask.mode)
            ? mask.mode[rawKey]
            : null;
          const modeType = typeof modeEntry === 'string'
            ? modeEntry
            : (modeEntry?.color || modeEntry?.type || '');
          const dynamicTagColor = Boolean(
            colorField
            && validRowColor
            && mask.appearance !== 'indicator'
            && mask.appearance !== 'dotIndicator'
          );
          const tagType = String(
            colorField
              ? (dynamicTagColor ? '' : 'default')
              : (modeType || rawKey || '')
          ).trim();

          if (tagType || validRowColor) {
            const appearanceClass = mask.appearance === 'indicator'
              ? 'indicator'
              : mask.appearance === 'dotIndicator'
                ? 'indicator-dot'
                : 'tag';
            const label = colorField
              ? (elem.textContent || rawKey)
              : (
                (typeof modeEntry === 'object' && modeEntry?.text)
                  ? resolveDicRef(modeEntry.text)
                  : (
                    window.dic?.tags?.[tagType]
                    || window.dic?.tag?.[tagType]
                    || elem.textContent
                    || tagType
                  )
              );

            elem.textContent = label;
            elem.classList.add(appearanceClass);
            if (tagType) elem.classList.add(tagType);

            if (validRowColor) {
              elem.style.color = rowColor;
              elem.classList.add('dynamic-color');
            }

            if (appearanceClass === 'tag' && mask.appearance === 'dot') {
              elem.classList.add('pill', 'dot');
            } else if (appearanceClass === 'tag' && mask.appearance === 'pill') {
              elem.classList.add('pill');
            }
          }
          mask = null;
          break;
        }

        case 'color': {
          if (elem.tagName !== 'INPUT') {
            const raw = String(mask.value ?? elem.textContent ?? '').trim();
            const presentation = String(mask.presentation || 'hex').trim().toLowerCase();
            const automatic = raw === '' || /^(auto|automatic)$/i.test(raw);
            const label = automatic ? globalThis.dic?.colors?.auto ?? '' : formatColor(raw, presentation);
            elem.textContent = label;
            elem.classList.remove('left', 'right');
            elem.classList.add(mask.align || 'center');
            if (!automatic && ['box', 'circle', 'cicle'].includes(presentation) && parseColor(raw)) {
              const swatch = document.createElement('span');
              swatch.className = 'ep-color-swatch';
              if (presentation !== 'box') swatch.classList.add('is-circle');
              swatch.style.backgroundColor = formatColor(raw, 'hex');
              swatch.title = formatColor(raw, 'rgba');
              swatch.setAttribute('role', 'img');
              swatch.setAttribute('aria-label', swatch.title);
              elem.replaceChildren(swatch);
            }
            mask = null;
            break;
          }
          mask.format = 'xxxxxxxx';
          mask.patterns = { x: '[0-9A-Fa-f]' };
          mask.align = 'right';
          mask.placeholder = 'FFFFFFFF';
          break;
        }

        case 'cpf':
          mask.format = '000.000.000-00';
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : mask.format;
          break;

        case 'cnpj':
          mask.format = 'AA.AAA.AAA/AAAA-00';
          mask.patterns = { ...mask.patterns, A: '[A-Za-z0-9]' };
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : mask.format;
          break;

        case 'creditCard':
          const value = elem.tagName === 'INPUT' ? elem.value : elem.textContent;
          const cc = {
            visa: /^4[0-9]{12}(?:[0-9]{3})/,
            mastercard: /^5[1-5][0-9]{14}/,
            amex: /^3[47][0-9]{13}/,
            elo: /^(((636368|438935|504175|451416|636297)[0-9]{10})|((5067|4576|4011)[0-9]{12}))/,
            hipercard: /^((606282[0-9]{10}([0-9]{0,3})?)|(3841[0-9]{12,15}))/,
            dinersClub: /^3(?:0[0-5]|[68][0-9])[0-9]{11}/,
            discover: /^6(?:011|5[0-9]{2})[0-9]{12}/,
            jcb: /^(?:2131|1800|35\d{3})\d{11}/
          };

          mask.format = '0000 0000 0000 0999 999';
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : mask.format;


          // for(const card in cc){
          //   if(cc[card].test(value)){
          //     mask.icon = {type: 'ep', name: `cc ${card}`}   
          //   }
          // }
          break;

        case 'date':
          const placeholder = mask.placeholder !== undefined ? mask.placeholder : dic.date.mask.placeholder;
          mask = { ...dic.date.mask };
          mask.placeholder = placeholder;
          mask.template = 'date';
          break;

        case 'dateTime':
          mask.format = 'D0/M0/0000 - H0:I0';
          mask.patterns = { D: /[0-2]/, M: /[0-3]/, H: /[0-2]/, I: /[0-5]/, 0: /[0-9]/ };
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : '00/00/0000 - 00:00';
          break;

        case 'document':
        case 'geodoc': {
          const { getCountryDocument } = await import('../../../shared/core/documentIdentity.js');
          const rawCountry = mask.country || options.country || options.data?.document_country || options.data?.country || options.row?.document_country || 'BR';
          const entity = mask.entity || options.entity || (mask.organization ? 'organization' : 'person');
          const doc = getCountryDocument(rawCountry, { entity });
          if (doc?.mask) {
            mask.format = doc.mask;
            mask.charset = doc.charset;
            if (doc.charset === 'alnum') {
              mask.patterns = { ...mask.patterns, A: '[A-Za-z0-9]' };
            }
          }
          mask.align = 'center';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : mask.format;
          break;
        }

        case 'ipv4':
          mask.format = '0ZZ.0ZZ.0ZZ.0ZZ';
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : '0.0.0.0';
          mask.patterns = { Z: /[0-9]/ };
          mask.size = 15;
          break;

        case 'currency':
        case 'float':
        case 'integer':
        case 'number':
        case 'percent':
          if (mask.size === undefined) mask.size = 10;

          if (mask.template === 'integer') {
            mask.decimalPlaces = 0;
          } else if (mask.template === 'number' && mask.decimalPlaces === undefined) {
            mask.decimalPlaces = 0;
          }
          const decimalPlaces = typeof mask.decimalPlaces === 'number'
            ? mask.decimalPlaces
            : window.dic.number.decimalPlaces;

          const currency = mask.template === 'currency' || mask.currency === true ? true : false;
          const percent = mask.template === 'percent' || mask.percent === true ? true : false;

          mask.format = 'A'.repeat(80);
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : false;
          mask.decimalPlaces = decimalPlaces;
          mask.currency = currency;
          mask.percent = percent;
          mask.fn = function (event) {
            const input = event.currentTarget;
            const options = input.maskOptions ?? {};
            const decimalPlaces = Number.isInteger(options.decimalPlaces) && options.decimalPlaces >= 0
              ? options.decimalPlaces
              : 2;
            const currency = options.template === 'currency' || options.currency === true;
            const percent = options.template === 'percent' || options.percent === true;

            const extractDigits = (value) => (value || '').replace(/\D/g, '');
            const getRawDigits = () => {
              if (typeof input.dataset.rawValue === 'string') {
                return input.dataset.rawValue;
              }
              return extractDigits(input.value);
            };

            const caretToRawIndex = (caret, formatted) => {
              const decimalPoint = String(globalThis?.dic?.number?.decimalPoint ?? ',');
              const decimalIndex = decimalPlaces > 0 && decimalPoint
                ? formatted.lastIndexOf(decimalPoint)
                : -1;
              const integerEnd = decimalIndex >= 0 ? decimalIndex : formatted.length;
              const boundedCaret = Math.max(0, Math.min(caret, formatted.length));
              let integerDigits = 0;

              for (let i = 0; i < Math.min(boundedCaret, integerEnd); i++) {
                if (/\d/.test(formatted[i])) integerDigits++;
              }

              const rawValue = getRawDigits();
              const rawIntegerDigits = decimalPlaces > 0
                ? Math.max(0, rawValue.length - decimalPlaces)
                : rawValue.length;
              const logicalIntegerIndex = Math.min(integerDigits, rawIntegerDigits);

              if (boundedCaret <= integerEnd || decimalIndex < 0) {
                return logicalIntegerIndex;
              }

              let decimalDigits = 0;
              for (let i = decimalIndex + 1; i < boundedCaret; i++) {
                if (/\d/.test(formatted[i])) decimalDigits++;
              }
              return Math.min(rawIntegerDigits + decimalDigits, rawValue.length);
            };

            const applyFormat = (digits, rawCaretIndex = null) => {
              const cleaned = extractDigits(digits);
              const normalized = cleaned.replace(/^0+(?=\d)/, '');

              if (normalized.length === 0) {
                delete input.dataset.rawValue;
                input.value = '';
              } else {
                input.dataset.rawValue = normalized;

                const padded = decimalPlaces > 0
                  ? normalized.padStart(decimalPlaces + 1, '0')
                  : normalized;

                let formatted;
                if (decimalPlaces > 0) {
                  const intPart = padded.slice(0, -decimalPlaces) || '0';
                  const decPart = padded.slice(-decimalPlaces);
                  formatted = format.number(`${intPart}.${decPart}`, {
                    decimalPlaces,
                    currency,
                    percent
                  });
                } else {
                  formatted = format.number(padded, {
                    decimalPlaces,
                    currency,
                    percent
                  });
                }

                input.value = formatted;
              }

              if (rawCaretIndex === null || typeof input.setSelectionRange !== 'function') return;

              const decimalPoint = String(globalThis?.dic?.number?.decimalPoint ?? ',');
              const thousandSeparator = String(globalThis?.dic?.number?.thousandSeparator ?? '.');
              const decimalIndex = decimalPlaces > 0 && decimalPoint
                ? input.value.lastIndexOf(decimalPoint)
                : -1;
              const integerEnd = decimalIndex >= 0 ? decimalIndex : input.value.length;
              const paddingDigits = decimalPlaces > 0
                ? Math.max(0, decimalPlaces + 1 - normalized.length)
                : 0;
              const paddedRawCaretIndex = Math.min(
                paddingDigits + rawCaretIndex,
                normalized.length + paddingDigits
              );
              const integerDigits = decimalPlaces > 0
                ? Math.max(1, normalized.length + paddingDigits - decimalPlaces)
                : normalized.length;
              const logicalIntegerIndex = Math.min(paddedRawCaretIndex, integerDigits);
              let seenDigits = 0;
              let desiredCaret = 0;

              for (let i = 0; i < integerEnd; i++) {
                const char = input.value[i];
                if (/\d/.test(char)) seenDigits++;
                desiredCaret = i + 1;
                if (seenDigits >= logicalIntegerIndex) break;
              }

              if (logicalIntegerIndex >= integerDigits) {
                desiredCaret = integerEnd;
              }

              if (paddedRawCaretIndex > integerDigits && decimalIndex >= 0) {
                const decimalOffset = Math.min(paddedRawCaretIndex - integerDigits, decimalPlaces);
                desiredCaret = Math.min(decimalIndex + 1 + decimalOffset, input.value.length);
              }

              if (thousandSeparator && input.value.slice(0, desiredCaret).endsWith(thousandSeparator)) {
                desiredCaret++;
              }

              input.setSelectionRange(desiredCaret, desiredCaret);
            };

            const type = event.inputType;
            const selectionStart = input.selectionStart ?? input.value.length;
            const selectionEnd = input.selectionEnd ?? selectionStart;
            const hasSelection = selectionEnd > selectionStart;
            const replacesAll = hasSelection && (selectionEnd - selectionStart) >= input.value.length;

            const formattedValue = input.value || '';
            const rawValue = getRawDigits();
            const rawStart = caretToRawIndex(selectionStart, formattedValue);
            const rawEnd = caretToRawIndex(selectionEnd, formattedValue);
            let newRawValue = rawValue;
            let newRawCaret = null;

            switch (type) {
              case 'historyUndo':
              case 'historyRedo':
                return;
              case 'deleteContentBackward':
              case 'deleteContentForward':
              case 'deleteByCut':
              case 'deleteByDrag': {
                if (hasSelection || replacesAll) {
                  const prefix = rawValue.slice(0, rawStart);
                  const suffix = rawValue.slice(rawEnd);
                  newRawValue = prefix + suffix;
                  newRawCaret = prefix.length;
                } else if (type === 'deleteContentBackward') {
                  if (rawStart > 0) {
                    newRawValue = rawValue.slice(0, rawStart - 1) + rawValue.slice(rawStart);
                    newRawCaret = rawStart - 1;
                  } else {
                    return;
                  }
                } else if (type === 'deleteContentForward') {
                  if (rawStart < rawValue.length) {
                    newRawValue = rawValue.slice(0, rawStart) + rawValue.slice(rawStart + 1);
                    newRawCaret = rawStart;
                  } else {
                    return;
                  }
                } else {
                  return;
                }
                break;
              }
              case 'insertReplacementText': {
                const inserted = extractDigits(event.data);
                if (hasSelection || replacesAll) {
                  const prefix = rawValue.slice(0, rawStart);
                  const suffix = rawValue.slice(rawEnd);
                  newRawValue = inserted.length > 0 ? prefix + inserted + suffix : prefix + suffix;
                  newRawCaret = prefix.length + inserted.length;
                } else {
                  if (inserted.length === 0) return;
                  const prefix = rawValue.slice(0, rawStart);
                  const suffix = rawValue.slice(rawStart);
                  newRawValue = prefix + inserted + suffix;
                  newRawCaret = rawStart + inserted.length;
                }
                break;
              }
              case 'insertFromPaste': {
                const inserted = extractDigits(event.data);
                const prefix = rawValue.slice(0, hasSelection ? rawStart : rawStart);
                const suffix = rawValue.slice(hasSelection ? rawEnd : rawStart);
                if (inserted.length === 0 && (hasSelection || replacesAll)) {
                  newRawValue = prefix + suffix;
                  newRawCaret = prefix.length;
                } else if (inserted.length > 0) {
                  newRawValue = prefix + inserted + suffix;
                  newRawCaret = prefix.length + inserted.length;
                } else {
                  return;
                }
                break;
              }
              case 'insertText':
              case 'insertCompositionText': {
                if (!event.data) {
                  return;
                }

                const inserted = extractDigits(event.data);
                if (inserted.length === 0) {
                  if (event.cancelable) {
                    event.preventDefault();
                  }
                  return;
                }

                const prefix = rawValue.slice(0, rawStart);
                const suffix = rawValue.slice(hasSelection ? rawEnd : rawStart);
                if (hasSelection || replacesAll) {
                  newRawValue = prefix + inserted + suffix;
                  newRawCaret = prefix.length + inserted.length;
                } else {
                  newRawValue = prefix + inserted + suffix;
                  newRawCaret = rawStart + inserted.length;
                }
                break;
              }
              default:
                return;
            }

            if (event.cancelable) {
              event.preventDefault();
            }

            if (newRawCaret === null) {
              if (hasSelection || replacesAll) {
                newRawCaret = rawStart;
              } else {
                newRawCaret = newRawValue.length;
              }
            }

            applyFormat(newRawValue, newRawCaret);
          };

          mask.init = function (el) {
            const syncFromValue = () => {
              const sourceValue = String(el.value || '').trim();
              const numericValue = Number(sourceValue);
              const rawDigits = Number.isFinite(numericValue)
                ? Math.round(Math.abs(numericValue) * (10 ** decimalPlaces)).toFixed(0)
                : '';

              if (!rawDigits) {
                delete el.dataset.rawValue;
                if (!el.value || el.value.trim() === '') {
                  el.value = '';
                }
                return;
              }

              const normalized = rawDigits.replace(/^0+(?=\d)/, '') || (rawDigits.includes('0') ? '0' : '');

              if (!normalized) {
                delete el.dataset.rawValue;
                el.value = '';
                return;
              }

              el.dataset.rawValue = normalized;

              const padded = decimalPlaces > 0
                ? normalized.padStart(decimalPlaces + 1, '0')
                : normalized;

              let formatted;
              if (decimalPlaces > 0) {
                const intPart = padded.slice(0, -decimalPlaces) || '0';
                const decPart = padded.slice(-decimalPlaces);
                formatted = format.number(`${intPart}.${decPart}`, {
                  decimalPlaces,
                  currency,
                  percent
                });
              } else {
                formatted = format.number(padded, {
                  decimalPlaces,
                  currency,
                  percent
                });
              }

              el.value = formatted;
            };

            syncFromValue();

            el.addEventListener('focus', () => {
              if (!el.dataset.rawValue && (!el.value || el.value.trim() === '')) {
                return;
              }

              const moveCaret = () => {
                if (typeof el.setSelectionRange === 'function') {
                  const end = el.value ? el.value.length : 0;
                  el.setSelectionRange(end, end);
                }
              };

              if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(moveCaret);
              } else {
                setTimeout(moveCaret, 0);
              }
            });
          };

          break;
        case 'phone':
          if (typeof mask.nationalMode === 'boolean' && mask.national === undefined) {
            mask.national = mask.nationalMode;
          }
          const { getMask } = await import('../create/phone');
          const responsePhone = await getMask(elem, options);
          mask.format = responsePhone.format;
          mask.icon = responsePhone.icon;
          mask.ddi = responsePhone.ddi;
          mask.value = responsePhone.value;
          mask.internationalFormat = responsePhone.internationalFormat;
          mask.internationalValue = responsePhone.internationalValue;
          mask.align = 'left';
          mask.placeholder = mask.placeholder !== undefined
            ? mask.placeholder
            : (responsePhone.placeholder !== undefined ? responsePhone.placeholder : mask.format);
          elem._maskLock = false;
          break;

        case 'progress':
          const opt = {
            element: 'progress',
            parent: elem,
            value: mask.value,
            max: mask.max,
            percentual: mask?.percentual,
            inSight: mask?.inSight,
            scaled: mask?.scaled,
            scale: mask?.scale,
            showMax: mask?.showMax
          }
          create(opt);
          break;

        case 'radio':
          const rvalue = elem.textContent;

          if (
            !Array.isArray(mask?.values) ||
            !Array.isArray(mask?.labels) ||
            mask.values.length !== mask.labels.length
          ) return;

          for (const i in mask.values) {
            if (rvalue === mask.values[i]) {
              elem.textContent = mask.labels[i];
            }
          }
          mask = null;
          break;

        case 'rg':
          mask.format = '00.000.000-A';
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : mask.format;
          break;

        case 'time':
          mask.format = '00:00';
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : mask.format;
          mask.fn = 'time';
          break;

        case 'timeFull':
          mask.format = '00:00:00';
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : mask.format;
          mask.fn = 'time';
          break;

        case 'timeTotal':
          mask.format = '000:00';
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : mask.format;
          mask.fn = 'timeTotal';
          break;

        case 'timeTotalFull':
          mask.format = '000:00:00';
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : mask.format;
          mask.fn = 'timeTotal';
          break;

        case 'timeTotalFullWithDays':
          mask.format = '000d00h00m00s';
          mask.align = 'right';
          mask.placeholder = mask.placeholder !== undefined ? mask.placeholder : mask.format;
          mask.fn = 'timeDays';
          break;

        case 'textOnly':
          elem.text(elem.text().replace(/[^A-Za-z\s]/, ''));
          elem.text(elem.text().replace(/\s+/, ' '));
          mask.format = 'T'.repeat(mask.max);
          mask.patterns = { T: /[A-Za-z\s]/ };
          break;
      }
    }

  if (mask) {
    // Controles editáveis preservam números e datas à direita. Em uma grid,
    // porém, estes mesmos valores são conteúdo de célula e seguem o padrão
    // visual centralizado. A decisão é contextual, não da config da máscara.
    const isGridCell = elem.closest?.('td') !== null;
    const contextualAlignmentTemplates = new Set([
      'cep', 'cnpj', 'cpf', 'creditCard', 'date', 'dateTime', 'document', 'geodoc', 'phone', 'rg',
    ]);
    if (contextualAlignmentTemplates.has(mask.template)) {
      mask.align = isGridCell ? 'center' : 'right';
      if (isGridCell) {
        elem.classList.remove('left', 'right');
        elem.classList.add('center');
      }
    }

    if (elem.tagName === 'INPUT') {
      elem.mask(mask);
    } else {
      if (mask.template === 'document' || mask.template === 'geodoc') {
        const raw = String(elem.textContent ?? '').trim();
        if (raw) {
          const { getCountryDocument } = await import('../../../shared/core/documentIdentity.js');
          const rawCountry = mask.country || options.country || options.data?.document_country || options.data?.country || options.row?.document_country || 'BR';
          const entity = mask.entity || options.entity || (mask.organization ? 'organization' : 'person');
          const doc = getCountryDocument(rawCountry, { entity });
          if (doc?.mask) {
            const clean = doc.charset === 'digits'
              ? raw.replace(/\D/g, '')
              : raw.normalize('NFKC').toUpperCase().replace(/[^0-9A-Z]/g, '');
            let idx = 0;
            let output = '';
            for (const token of String(doc.mask)) {
              if (token === '0' || token === 'A') {
                let appended = false;
                while (idx < clean.length && !appended) {
                  const char = clean[idx++];
                  if (token === '0' && !/\d/.test(char)) continue;
                  output += char;
                  appended = true;
                }
                continue;
              }
              if (output && idx < clean.length) output += token;
            }
            elem.textContent = output || raw;
          }
        }
        elem.classList.add(mask.align || 'center');
        return elem;
      }

      if (mask.template === 'phone') {
        const isNational = mask.national === true;
        const phoneText = isNational
          ? (mask.value ?? '')
          : (mask.internationalValue ?? mask.value ?? '');

        elem.replaceChildren();

        if (!isNational && mask.icon) {
          const iconWrap = document.createElement('span');
          iconWrap.classList.add('phone-flag');
          setIcon(iconWrap, { icon: mask.icon });
          elem.appendChild(iconWrap);
        }

        const textWrap = document.createElement('span');
        textWrap.classList.add('phone-text');
        textWrap.textContent = String(phoneText || '').trim();
        elem.appendChild(textWrap);
        elem.classList.add(mask.align);
        return elem;
      }

      if (mask.template === 'date') {
        const raw = elem.textContent?.trim() ?? '';
        const match = raw.match(/^\d{4}-\d{2}-\d{2}/);
        if (match) {
          const dateStr = match[0];
            const formatted = format.date(dateStr);
            if (formatted) {
              elem.textContent = formatted.replace(/[^0-9]/g, '');
            }
          }
        }
        if (mask.icon) {
          const icon = setIcon(elem, mask);
          elem.parentElement.prepend(icon);
          elem.parentElement.classList.add('cell-wrap-grid');
        }
        if (mask.value) {
          elem.textContent = mask.value;
        }

        elem.maskOptions = mask;
        const maskObj = new Mask(elem);
        maskObj.set();
      }

    }
  }

  return elem;
};
